import React from 'react'
import { createPortal } from 'react-dom'
import { ApiError, ValidationError, api } from '../api/client'
import { useApp } from '../context/AppContext'
import { formatPrice, parsePrice, priceInput } from '../catalog/format'
import {
  RefundAttempt,
  RefundOutcome,
  amountProblem,
  keyForAttempt,
  refundOutcome,
  suggestedPackageRefund,
} from '../payments/refund'
import { saveErrorMessage, showToast } from '../screens/settings/toast'
import { useDialog } from '../hooks/useDialog'
import Field from '../screens/settings/Field'
import type { PaymentMethod } from '../types/app'
import './Sheet.css'
import './PayLinkSheet.css'
import '../screens/settings/Settings.css'

interface RefundSheetProps {
  paymentId: string
  maxPence: number
  // How the payment was taken, when not through Harmovi's card flow: she
  // gives that money back herself and the sheet only records it.
  recordedMethod?: PaymentMethod
  packageCredits?: { available: number; pricePaidPence: number; sessionsTotal: number }
  // The refund goes back to a card through Stripe: it says when it arrives.
  toCard?: boolean
  onClose: () => void
  onDone: () => void
}

// RefundSheet refunds a payment in full or in part: a card payment taken
// through Harmovi through Stripe, anything else (recordedMethod) by recording
// the money she gave back herself. For a package she can
// also take credits back; the amount follows the credits until she types one.
// One idempotency key is made per open and reused when she retries the same
// refund; changing the details makes a new key (see keyForAttempt).
// recordedNote says that Harmovi moves no money for this refund.
function recordedNote(method: PaymentMethod): string {
  switch (method) {
    case 'cash':
      return 'This was paid in cash, so hand the money back yourself. Harmovi only records the refund.'
    case 'bank_transfer':
      return 'This was paid by bank transfer, so send the money back yourself. Harmovi only records the refund.'
    default:
      return 'This card payment wasn’t taken through Harmovi, so refund it on your own card reader. Harmovi only records the refund.'
  }
}

export default function RefundSheet({ paymentId, maxPence, recordedMethod, packageCredits, toCard = false, onClose, onDone }: RefundSheetProps) {
  const { dispatch } = useApp()
  const [amount, setAmount] = React.useState(priceInput(maxPence))
  const [edited, setEdited] = React.useState(false)
  const [credits, setCredits] = React.useState(0)
  const [reason, setReason] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [fieldError, setFieldError] = React.useState('')
  const [error, setError] = React.useState('')
  const [outcome, setOutcome] = React.useState<RefundOutcome | null>(null)
  const attempt = React.useRef<RefundAttempt | null>(null)

  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, outcome ? onDone : onClose, { closable: !busy })

  const changeCredits = (next: number) => {
    setCredits(next)
    if (packageCredits && !edited) {
      const pence = next === 0 ? maxPence : suggestedPackageRefund(packageCredits.pricePaidPence, packageCredits.sessionsTotal, next)
      setAmount(priceInput(Math.min(pence, maxPence)))
    }
    setFieldError('')
    setError('')
  }

  const submit = async () => {
    const problem = amountProblem(amount, maxPence)
    if (problem) {
      setFieldError(problem)
      return
    }
    const details = {
      amount_pence: parsePrice(amount) as number,
      remove_credits: credits,
      reason: reason.trim() || null,
    }
    attempt.current = keyForAttempt(attempt.current, details, () => crypto.randomUUID())
    setBusy(true)
    setFieldError('')
    setError('')
    try {
      const result = await api.refund(paymentId, details, attempt.current.key)
      const next = refundOutcome(result)
      if (next.kind === 'failed') {
        attempt.current = { ...attempt.current, failed: true }
        setError(next.message)
      } else if (next.kind === 'done') {
        showToast(dispatch, recordedMethod ? `Refund of ${formatPrice(result.amount_pence)} recorded` : next.message)
        onDone()
        return
      } else {
        setOutcome(next)
      }
    } catch (e) {
      if (e instanceof ApiError && e.problem === 'refund-too-large') {
        setFieldError(`That’s more than can be refunded. Try a smaller amount.`)
      } else if (e instanceof ApiError && e.problem === 'payment-disputed') {
        setError('This payment is disputed, so it can’t be refunded until the dispute is settled.')
      } else if (e instanceof ApiError && e.problem === 'refund-failed') {
        attempt.current = { ...attempt.current, failed: true }
        setError("The refund didn’t go through. Nothing was refunded.")
      } else if (e instanceof ValidationError) {
        const fields = e.fields
        if (fields.amount_pence) setFieldError(fields.amount_pence)
        else setError(Object.values(fields)[0] ?? 'Check the details and try again.')
      } else {
        setError(saveErrorMessage(e))
      }
    }
    setBusy(false)
  }

  const pence = parsePrice(amount)
  const sheet = (
    <div
      className="sheet-overlay"
      onClick={(e) => {
        e.stopPropagation()
        if (!busy) (outcome ? onDone : onClose)()
      }}
    >
      <div ref={dialogRef} className="sheet-content" role="dialog" aria-modal="true" aria-label="Refund" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle"></div>
        <div className="sheet-header">
          <h2>Refund</h2>
        </div>
        <div className="sheet-body">
          {outcome ? (
            <p className="paylink__status" role="status">
              {outcome.message}
            </p>
          ) : (
            <div className="settings-form sheet-form">
              {recordedMethod && <p className="field-hint">{recordedNote(recordedMethod)}</p>}
              <Field id="refund-amount" label="Amount to refund (£)" hint={`The most you can refund is ${formatPrice(maxPence)}.`} error={fieldError}>
                <input
                  id="refund-amount"
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  disabled={busy}
                  onChange={(e) => {
                    setAmount(e.target.value)
                    setEdited(true)
                    setFieldError('')
                    setError('')
                  }}
                />
              </Field>
              {packageCredits && packageCredits.available > 0 && (
                <div className="field">
                  <span id="refund-credits-label">Sessions to remove</span>
                  <div className="booking-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-compact"
                      aria-label="Remove one fewer session"
                      disabled={busy || credits <= 0}
                      onClick={() => changeCredits(credits - 1)}
                    >
                      −
                    </button>
                    <span role="status" aria-labelledby="refund-credits-label">
                      {credits}
                    </span>
                    <button
                      type="button"
                      className="btn btn-secondary btn-compact"
                      aria-label="Remove one more session"
                      disabled={busy || credits >= packageCredits.available}
                      onClick={() => changeCredits(credits + 1)}
                    >
                      +
                    </button>
                  </div>
                  <p className="field-hint">{packageCredits.available} left on this package.</p>
                </div>
              )}
              <Field id="refund-reason" label="Reason (optional)">
                <textarea id="refund-reason" rows={2} maxLength={500} value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)} />
              </Field>
              {/* Word joiners keep "5–10" from breaking at the dash. */}
              {toCard && !recordedMethod && <p className="field-hint">Goes back to the card they paid with. Arrives in 5{'\u2060'}–{'\u2060'}10 days.</p>}
              {error && (
                <p className="field-error" role="alert">
                  {error}
                </p>
              )}
            </div>
          )}
        </div>
        {outcome ? (
          <button type="button" className="btn btn-quiet btn-block" onClick={onDone}>
            Done
          </button>
        ) : (
          // Money that can't come back: the final confirm is the only solid
          // red, with the way back below it (docs/25 danger pattern).
          <div className="sheet-stack">
            <button type="button" className="btn btn-danger-solid btn-block" onClick={submit} disabled={busy}>
              {busy
                ? recordedMethod ? 'Recording…' : 'Refunding…'
                : recordedMethod
                  ? pence && pence > 0 ? `Record ${formatPrice(pence)} refund` : 'Record refund'
                  : pence && pence > 0 ? `Refund ${formatPrice(pence)}` : 'Refund'}
            </button>
            <button type="button" className="btn btn-secondary btn-block" onClick={onClose} disabled={busy}>
              Don’t refund
            </button>
          </div>
        )}
      </div>
    </div>
  )

  const host = typeof document === 'undefined' ? null : document.querySelector('.app-container')
  return host ? createPortal(sheet, host) : sheet
}
